import { motion, AnimatePresence } from 'framer-motion';
import type { UnopenedChest } from '@services';
import shopChestNormal from '@/assets/shop/shop-chest-normal.webp';
import shopChestPlatinum from '@/assets/shop/shop-chest-platinum.webp';
import shopChestRare from '@/assets/shop/shop-chest-rare.webp';
import shopChestLegendary from '@/assets/shop/shop-chest-legendary.webp';
import CloseButton from '@/assets/svgs/CloseButton';

interface OpenChestModalProps {
  isOpen: boolean;
  chest: UnopenedChest | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

const chestImageMap: Record<number, string> = {
  1: shopChestNormal,
  2: shopChestPlatinum,
  3: shopChestRare,
  4: shopChestLegendary,
};

const chestRewardMap: Record<number, string> = {
  1: '1-3 Gold',
  2: '2-5 Gold',
  3: '4-8 Gold',
  4: '6-12 Gold',
};

export const OpenChestModal = ({
  isOpen,
  chest,
  onClose,
  onConfirm,
  isLoading = false,
}: OpenChestModalProps) => {
  if (!chest) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[110]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={!isLoading ? onClose : undefined}
            style={{ cursor: isLoading ? 'not-allowed' : 'default' }}
          />

          {/* Modal Container */}
          <div className="fixed left-0 right-0 top-1/2 -translate-y-1/2 z-[111] max-w-[var(--app-max-width)] mx-auto flex justify-center items-center pointer-events-none">
            <motion.div
              className="relative w-[339px] flex flex-col items-center p-3 isolate bg-[#C2CCDF] border-2 border-[rgba(46,64,86,0.2)] rounded-2xl pointer-events-auto"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              {/* Close Button */}
              <motion.div
                className="absolute top-3 right-3"
                whileHover={!isLoading ? { scale: 1.05, y: -2 } : {}}
                whileTap={!isLoading ? { scale: 0.95 } : {}}
                transition={{ duration: 0.15 }}
                onClick={!isLoading ? onClose : undefined}
                style={{
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.5 : 1,
                }}
              >
                <CloseButton />
              </motion.div>

              {/* Content */}
              <div className="flex flex-col items-center gap-4 w-full max-w-[307px] pt-5">
                {/* Chest Image Container - Overlapping */}
                <motion.img
                  src={chestImageMap[chest.chestId]}
                  alt={chest.chestName}
                  className=" size-24 object-contain z-10 absolute top-5 left-7"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 200,
                  }}
                />

                {/* Chest Info Container */}
                <div className="flex flex-col items-center mt-10 pt-5 pb-4 px-4 w-full bg-[#8391A6] rounded-xl gap-4">
                  {/* Name and Reward Row */}
                  <div className="flex justify-between items-start w-full gap-4">
                    {/* Left: Chest Name */}
                    <div className="flex-1 mt-6 ml-2">
                      <h2 className="font-lilita text-[24px] text-white text-left [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
                        {chest.chestName}
                      </h2>
                    </div>

                    {/* Right: Reward */}
                    <div className="flex flex-col items-end">
                      <p className="font-lilita text-[18px] text-white [text-shadow:1px_1px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
                        Reward
                      </p>
                      <p
                        className="font-lilita text-[22px] [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]"
                        style={{ color: '#FFE500' }}
                      >
                        {chestRewardMap[chest.chestId] || '100-500 Gold'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Open Button */}
                <div className="flex justify-center items-center w-full px-4 mb-2">
                  <motion.button
                    onClick={onConfirm}
                    disabled={isLoading}
                    className="relative flex items-center justify-center px-6 py-2 gap-3 isolate h-12 border-2 border-black rounded-xl overflow-hidden"
                    style={{
                      background:
                        'linear-gradient(135deg, #FF7A00 0%, #FFA500 49.04%, #FFD700 100%)',
                      opacity: isLoading ? 0.7 : 1,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      minWidth: '280px',
                    }}
                    whileHover={!isLoading ? { scale: 1.05, y: -2 } : {}}
                    whileTap={!isLoading ? { scale: 0.95 } : {}}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="absolute inset-x-2 top-2 h-7 bg-white/30 rounded-lg z-0" />
                    <span className="font-lilita text-[28px] leading-8 text-white relative z-10 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
                      {isLoading ? 'Opening...' : 'Open'}
                    </span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
